import { LitElement, css, html, nothing, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import styles from './toast.scss?inline';
import { SIZES } from '@/components/icons/constants';
import { DEFAULT_ICON_SIZE, DEFAULT_POSITION, EVENTS, ICONS, STATUS } from './constants';

export default class SotbitToast extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        status: { type: String, reflect: true },
        position: { type: String, reflect: true },
        heading: { type: String },
        message: { type: String },
        closable: { type: Boolean, reflect: true },
        iconSize: { type: String, attribute: 'icon-size' },
        actionText: { type: String, attribute: 'action-text' },
        actionHref: { type: String, attribute: 'action-href' },
        _visible: { type: Boolean, state: true },
    };

    constructor() {
        super();

        this.status = STATUS.ALERT;
        this.position = DEFAULT_POSITION;
        this.heading = '';
        this.message = '';
        this.closable = true;
        this.iconSize = DEFAULT_ICON_SIZE;
        this.actionText = '';
        this.actionHref = '';
        this._visible = false;
        this._closing = false;
        this._actionHandler = null;

        this._handleCloseClick = this._handleCloseClick.bind(this);
        this._handleActionClick = this._handleActionClick.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        this._syncIconSizeVar();
    }

    willUpdate(changedProperties) {
        if (changedProperties.has('iconSize')) {
            this._syncIconSizeVar();
        }
    }

    firstUpdated() {
        requestAnimationFrame(() => {
            this._visible = true;
        });
    }

    setActionHandler(handler) {
        this._actionHandler = typeof handler === 'function' ? handler : null;
    }

    close() {
        if (this._closing) {
            return;
        }

        this._closing = true;
        this._visible = false;

        let finished = false;
        const finish = () => {
            if (finished) {
                return;
            }
            finished = true;
            this.dispatchEvent(
                new Event(EVENTS.CLOSE, {
                    bubbles: true,
                    composed: true,
                })
            );
            this.remove();
        };

        this.addEventListener('transitionend', finish, { once: true });
        setTimeout(finish, 250);
    }

    _resolveIconSizePx(size = this.iconSize) {
        const numeric = parseInt(size, 10);

        if (!Number.isNaN(numeric) && String(numeric) === String(size).trim()) {
            return numeric;
        }

        return SIZES[String(size || DEFAULT_ICON_SIZE).toUpperCase()] || SIZES.SL;
    }

    _syncIconSizeVar() {
        this.style.setProperty('--toast-icon-size', `${this._resolveIconSizePx()}px`);
    }

    _handleCloseClick(event) {
        event.stopPropagation();
        this.close();
    }

    _handleActionClick(event) {
        if (this._actionHandler) {
            event.preventDefault();
            this._actionHandler(event);
        }

        this.dispatchEvent(
            new CustomEvent(EVENTS.ACTION, {
                bubbles: true,
                composed: true,
                detail: { originalEvent: event },
            })
        );
    }

    renderIcon() {
        const iconId = ICONS[this.status] || ICONS[STATUS.ALERT];

        return html`
            <div class="toast__icon">
                <sotbit-icon icon="${iconId}" size="${this.iconSize}" no-padding></sotbit-icon>
            </div>
        `;
    }

    renderAction() {
        if (!this.actionText) {
            return html` <slot name="action"></slot> `;
        }

        if (this.actionHref) {
            return html`
                <a class="toast__action" href="${this.actionHref}" @click="${this._handleActionClick}">
                    ${this.actionText}
                </a>
            `;
        }

        return html`
            <button type="button" class="toast__action" @click="${this._handleActionClick}">${this.actionText}</button>
        `;
    }

    renderTitle() {
        if (this.heading) {
            return html` <div class="toast__title">${this.heading}</div> `;
        }

        return html` <slot name="title"></slot> `;
    }

    renderMessage() {
        const hasHeading = Boolean(this.heading);
        const messageClasses = {
            toast__message: true,
            'toast__message--secondary': hasHeading,
        };

        if (this.message) {
            return html` <div class="${classMap(messageClasses)}">${unsafeHTML(this.message)}</div> `;
        }

        return html` <slot name="message"></slot> `;
    }

    render() {
        const toastClasses = {
            toast: true,
            'toast--visible': this._visible,
        };

        return html`
            <div class="${classMap(toastClasses)}" role="status" aria-live="polite">
                <div class="toast__body">
                    ${this.renderIcon()}
                    <div class="toast__content">
                        <div class="toast__text">${this.renderTitle()}${this.renderMessage()}</div>
                        ${this.renderAction()}
                    </div>
                </div>
                ${this.closable
                    ? html`
                          <div class="toast__close">
                              <sotbit-icon
                                  variant="secondary"
                                  icon="close"
                                  pointer
                                  @click="${this._handleCloseClick}"
                              ></sotbit-icon>
                          </div>
                      `
                    : nothing}
            </div>
        `;
    }
}
